import { ArrowRight } from 'react-feather'
import { LoadingButton } from '@mui/lab'
import {
    TextField,
    Typography,
    Stack,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useFormik } from 'formik'
import api from 'src/Api'
import React, { useEffect, useState } from 'react'
import * as Yup from 'yup'
import { ToastContainer, toast } from 'react-toastify'

export default function Step2({ setActiveStep, user, setUnVerifiedSteps }) {
    const [submitting, setSubmitting] = useState(false)
    const [categories, setCategories] = useState([])
    const [company_name, setCompanyName] = useState('')

	const theme = useTheme()

    const formik = useFormik({
        initialValues: {
            description: user?.description || '',
            siret: user?.siret ?? '',
            company_name: user?.company_name || '',
            category: user?.category ?? '',
        },
        validationSchema: Yup.object().shape({
            company_name: Yup.string()
                .required('Ce champ est obligatoire *'),
            siret: Yup.string()
                .matches(/^\d{9}(\d{5})?$/, 'Le SIRET ou le SIREN doit contenir exactement 9 ou 14 chiffres')
                .required('Ce champ est obligatoire *'),
            category: Yup.string().required('ce champ est obligatoire *'),
        }),
        enableReinitialize: true,
        onSubmit: async (values, helpers) => {
            try {
                submit(values)
                setActiveStep(6)
            } catch (err) {
                console.error(err)
                helpers.setStatus({ success: false })
                helpers.setErrors({ submit: err.message })
                helpers.setSubmitting(false)
            }
        },
    })

    const submit = body => {
        setSubmitting(true)
        api.post('/users/submit_step', { ...body, step: 2 })
            .then(res => {
                setUnVerifiedSteps(res.data.unverified_steps)
                setSubmitting(false)
                showSuccess('Opération réussie')
            })
            .catch(err => {
                setSubmitting(false)
                console.log(err)
                showError(err.response.data.error)
            })
    }

    const showError = msg => {
        toast.error(msg, {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
        })
    }

    const showSuccess = msg => {
        toast.success(msg, {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
        })
    }

    const getCategories = () => {
        api.get('/admin/get_jobs_categories')
            .then(res => {
                setCategories(res.data)
                console.log(res.data)
            })
            .catch(err => { })
    }

    // Fonction pour récupérer le nom de l'entreprise
    const fetchCompanyName = async (siret) => {
        try {
            const response = await api.get(`/users/get_company_infos/${siret}`)
            if (response.data) {
                formik.setFieldValue('company_name', response.data.etablissement.uniteLegale.denominationUniteLegale)
                setCompanyName(response.data.etablissement.uniteLegale.denominationUniteLegale)
            }
        } catch (error) {
            if (siret.length === 9) {
                formik.setFieldError('siret', "Le SIREN est invalide.")
            } else if (siret.length === 14) {
                formik.setFieldError('siret', "Le SIRET est invalide.")
            }
        }
    }

    useEffect(() => {
        getCategories()
    }, [])

    // Gestion de la saisie du SIRET/SIREN
    const handleSiretChange = (event) => {
        const { value } = event.target
        // Supprimer les espaces du SIRET/SIREN
        const sanitizedValue = value.replace(/\s+/g, '')
        formik.setFieldValue('siret', sanitizedValue)
        if (sanitizedValue.length === 9 || sanitizedValue.length === 14) {
            fetchCompanyName(sanitizedValue) // Récupérer le nom de l'entreprise lorsque le SIRET/SIREN est valide
        } else {
            formik.setFieldValue('company_name', '')
        }
    }

    const checkIbanValidity = async (iban) => {
        try {
            const response = await api.get(`/users/get_iban_infos/${iban}`)
            return response.data.valid
        } catch (error) {
            showError('Erreur lors de la vérification de l\'IBAN')
            return false
        }
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            <Box sx={{ width: '100%', textAlign: 'center', mb: 4 }}>
                <Typography variant="h3" component="h1" gutterBottom>
                    Informations générales
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    insérer vos informations générales
                </Typography>
            </Box>

            <form autoComplete="off" noValidate onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
                <Stack spacing={3} sx={{ alignItems: 'center', width: '100%' }}>
                    <TextField
                        fullWidth
                        type="text"
                        name="siret"
                        label="SIRET ou SIREN"
                        onChange={handleSiretChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.siret}
                        error={Boolean(formik.touched.siret && formik.errors.siret)}
                        helperText={formik.touched.siret && formik.errors.siret}
                        sx={{
                            width: '60%',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '6px'
                            }
                        }}
                    />

                    <TextField
                        fullWidth
                        autoComplete="company_name"
                        type="text"
                        disabled
                        name="company_name"
                        label="Nom de l'entreprise ou société"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.company_name}
                        error={Boolean(formik.touched.company_name && formik.errors.company_name)}
                        helperText={formik.touched.company_name && formik.errors.company_name}
                        InputProps={{ readOnly: true }}
                        sx={{
                            width: '60%',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '6px'
                            }
                        }}
                    />

                    <TextField
                        fullWidth
                        multiline
                        type="text"
                        rows={2}
                        name="description"
                        label="Description (facultatif)"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.description}
                        error={Boolean(formik.touched.description && formik.errors.description)}
                        helperText={formik.touched.description && formik.errors.description}
                        sx={{
                            width: '60%',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '6px'
                            }
                        }}
                    />

                    <Box sx={{ width: '60%', mt: 2 }}>
                        <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', mb: 0 }}>
                            Services fournis :
                        </Typography>
                    </Box>

                    <FormControl fullWidth sx={{ width: '60%' }}>
                        <InputLabel id="service-category-label">Choisissez votre service principal</InputLabel>
                        <Select
                            labelId="service-category-label"
                            id="service-category"
                            value={formik.values.category}
                            label="Choisissez votre service principal"
                            name="category"
                            onChange={formik.handleChange}
                            error={Boolean(formik.touched.category && formik.errors.category)}
                            sx={{
                                borderRadius: '6px'
                            }}
                        >
                            {categories.length > 0 &&
                                categories.map(e => <MenuItem key={e._id} value={e._id}>{e.name}</MenuItem>)}
                        </Select>
                    </FormControl>

                    <LoadingButton
                        type="submit"
                        loading={submitting}
                        variant="contained"
                        sx={{
                            width: '60%',
                            mt: 3,
                            py: 2,
                            backgroundColor: theme.palette.primary.main,
                            borderRadius: '6px',
                            '&:hover': {
                                backgroundColor: theme.palette.primary.dark,
                            }
                        }}
                    >
                        Suivant
                    </LoadingButton>
                </Stack>
            </form>
            <ToastContainer
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </Box>
    )
}